import { VALIDATION_PATTERNS } from './constants';

/**
 * Validate an email address
 * @param {string} email - Email address to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidEmail = (email) => {
  if (!email) return false;
  return VALIDATION_PATTERNS.EMAIL.test(email);
};

/**
 * Validate a password
 * @param {string} password - Password to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidPasswordForLogin = (password) => {
  return !!password;
};

/**
 * Validate a password
 * @param {string} password - Password to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidPasswordForSignup = (password) => {
  if (!password) return false;
  // Accept at least 8 characters, at least one uppercase, one lowercase, one number
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d_]{8,}$/.test(password);
};

/**
 * Validate a phone number
 * @param {string} phone - Phone number to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidPhone = (phone) => {
  if (!phone) return false;
  return VALIDATION_PATTERNS.PHONE.test(phone);
};

/**
 * Validate a license plate
 * @param {string} plate - License plate to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidLicensePlate = (plate) => {
  if (!plate) return false;
  return VALIDATION_PATTERNS.LICENSE_PLATE.test(plate);
};

/**
 * Validate a PIN code
 * @param {string} pin - PIN code to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidPin = (pin) => {
  if (!pin) return false;
  return VALIDATION_PATTERNS.PIN.test(pin);
};

/**
 * Get validation error message for a field
 * @param {string} fieldName - Name of the field
 * @param {string} value - Value to validate
 * @param {string} context - Validation context
 * @returns {string|null} Error message or null if valid
 */
export const getValidationError = (fieldName, value, context = 'default') => {
  switch (fieldName) {
    case 'email':
      return isValidEmail(value) ? null : 'Please enter a valid email address';
    case 'password':
      if (context === 'login') {
        return isValidPasswordForLogin(value) ? null : 'Password is required';
      } else {
        return isValidPasswordForSignup(value)
        ? null 
        : 'Password must be at least 8 characters with at least one uppercase letter, one lowercase letter, and one number';
      }
    case 'phone':
      return isValidPhone(value) ? null : 'Please enter a valid phone number in format (XXX) XXX-XXXX';
    case 'licensePlate':
      return isValidLicensePlate(value) ? null : 'Please enter a valid license plate (up to 8 alphanumeric characters)';
    case 'pin':
      return isValidPin(value) ? null : 'PIN must be exactly 4 digits';
    default:
      return null;
  }
};

/**
 * Validate a form object with multiple fields
 * @param {object} formData - Form data object
 * @param {array} requiredFields - Array of required field names
 * @param {string} context - Validation context
 * @returns {object} Object with errors for each field
 */
export const validateForm = (formData, requiredFields = [], context = 'default') => {
  const errors = {};
  
  // Check required fields
  requiredFields.forEach(field => {
    if (!formData[field]) {
      errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
    }
  });
  
  // Validate fields with specific validation
  Object.keys(formData).forEach(field => {
    if (formData[field] && !errors[field]) {
      const error = getValidationError(field, formData[field], context);
      if (error) {
        errors[field] = error;
      }
    }
  });
  
  return errors;
};

/**
 * Check if a form has any validation errors
 * @param {object} errors - Errors object from validateForm
 * @returns {boolean} True if form has errors, false otherwise
 */
export const hasFormErrors = (errors) => {
  return Object.keys(errors).length > 0;
};
